import { useHistory, useLocation } from 'react-router-dom';
import { useEffect, useMemo, useState } from 'react';
import moment from 'moment';
import Container from '~/components/container';
import * as St from '../../styles';
import ContainerFlex from '~/components/containerFlex';
import { icons } from '~/assets';
import Input from '~/components/input';
import Loading from '~/components/loading';
import Button from '~/components/button';
import PictureModal from '~/components/pictureModal';
import { ImageZoomRotate } from '~/components/ImageZoomRotate';
import Dialog from '~/components/dialog';
import { OriginBiometrics } from '../..';
import useApi, { ApiMethod } from '~/hooks/useApi';
import Endpoints from '~/services/endpoints';

interface IBankNowBiometricDetails {
  name?: string;
  code?: string;
  status?: string;
  createdDate?: string;
  flow?: string;
  faceImageKey?: string;
  processId?: string;
  imageBase64?: string;
}

interface BankNowDetailState {
  id?: unknown;
  biometriaType?: string;
  cpf?: string;
  item?: IBankNowBiometricDetails;
}

interface Props {
  data?: IBankNowBiometricDetails | null;
  imageBase64?: string;
  cpf?: string;
}

interface ImageProps {
  face: string;
}

const BankNowBiometricsDetail: React.FC<Props> = ({ data: dataProp, imageBase64, cpf: cpfProp }) => {
  const { state } = useLocation<BankNowDetailState>();
  const history = useHistory();

  const dataFromState =
    state?.item ??
    (typeof state?.id === 'object' ? (state?.id as IBankNowBiometricDetails) : null);

  const data = useMemo<IBankNowBiometricDetails | null>(() => {
    return dataProp ?? dataFromState ?? null;
  }, [dataProp, dataFromState]);

  const cpf = cpfProp ?? state?.cpf;

  const [openDialog, setOpenDialog] = useState(false);
  const [image, setImage] = useState<string>(imageBase64 ?? data?.imageBase64 ?? '');
  const [imageLoaded, setImageLoaded] = useState<boolean>(Boolean(imageBase64 ?? data?.imageBase64));
  const [isFetchingImage, setIsFetchingImage] = useState(false);

  const [dialogError, setDialogError] = useState(false);
  const [messageError, setMessageError] = useState('');

  const originLabel = (origin?: string) => origin || '-';

  const dateTimeStr = useMemo(() => {
    return data?.createdDate && moment(data.createdDate).isValid()
      ? moment(data.createdDate).format('DD/MM/YYYY HH:mm:ss')
      : '-';
  }, [data?.createdDate]);

  const imgSrc = useMemo(() => {
    if (!image) return icons.userAvatar as string;
    if (/^https?:\/\//i.test(image)) return image;
    if (image.startsWith('data:image')) return image;
    return `data:image/jpeg;base64,${image}`;
  }, [image]);

  const RequestBankNowBiometricImage = useApi<ImageProps>(
    Endpoints.integration.getImageBankNowBiometric(data?.faceImageKey),
    ApiMethod.GET,
  );

  const getBankNowBiometricImage = async () => {
    try {
      setIsFetchingImage(true);
      const response = await RequestBankNowBiometricImage.callApi();
      const face = response?.data?.face;
      if (face) {
        setImage(face);
        setImageLoaded(true);
      }
    } finally {
      setIsFetchingImage(false);
    }
  };

  useEffect(() => {
    if (!data) {
      setDialogError(true);
      setMessageError('Dados da biometria não encontrados.');
    }
  }, [data]);

  useEffect(() => {
    if (openDialog && !imageLoaded && !isFetchingImage) {
      if (data && data?.faceImageKey) getBankNowBiometricImage();
    }
  }, [openDialog, imageLoaded, data]);

  const handleTogglePicture = () => setOpenDialog((prev) => !prev);

    const goBack = () => {
    history.push('/consult/user/biometrics', {
      fromDetail: true,
    });
  }

  return (
    <>
      {openDialog && (
        <PictureModal handleClose={handleTogglePicture}>
          <div style={{ position: 'relative', overflow: 'hidden', background: '#fff', height: '100%' }}>
            <ImageZoomRotate image={imgSrc} />
          </div>
        </PictureModal>
      )}

      <Loading status={isFetchingImage} />

      <Dialog
        icon={icons.exclamation}
        open={dialogError}
        positiveLabel="Entendi"
        positiveAction={() => {
          setDialogError(false);
          history.push('/consult/user/biometrics');
        }}
        message={messageError}
      />

      <Container>
        <St.BoxCard>
          <ContainerFlex padding={30}>
            <ContainerFlex row center marginBottom={40}>
              <St.Icon src={icons.exclamation} />
              <St.TitleHeader>
                <span style={{ color: '#e71037' }}>Detalhes biometria facial</span>
                <img
                  src={imgSrc}
                  alt=""
                  style={{ height: 40, width: 40, borderRadius: '50%', objectFit: 'cover', marginLeft: 12 }}
                />
              </St.TitleHeader>
            </ContainerFlex>

            {data ? (
              <ContainerFlex center>
                <div style={{ width: '100%', maxWidth: 520 }}>
                  <St.Subtitle style={{ textAlign: 'center' }}>Dados da Biometria</St.Subtitle>

                  <ContainerFlex marginTop={20}>
                    <Input readOnly label="Resultado" value={data?.status} />
                  </ContainerFlex>

                  <ContainerFlex marginTop={20}>
                    <Input readOnly label="Nome Completo" value={data?.name || '-'} />
                  </ContainerFlex>

                  <ContainerFlex marginTop={20}>
                    <Input readOnly label="Origem Biometria" value={OriginBiometrics(data?.flow) || '-'} />
                  </ContainerFlex>

                  <ContainerFlex marginTop={20}>
                    <Input readOnly label="Data e Hora" value={dateTimeStr} />
                  </ContainerFlex>

                  {cpf && (
                    <ContainerFlex marginTop={20}>
                      <Input readOnly label="CPF" value={cpf} />
                    </ContainerFlex>
                  )}
                </div>
              </ContainerFlex>
            ) : (
              <ContainerFlex center>
                <span>Nenhum dado encontrado</span>
              </ContainerFlex>
            )}

            <ContainerFlex row end marginTop={30} gap="30">
              <Button outlined fitContent onClick={goBack}>
                Voltar
              </Button>
              <Button fitContent onClick={handleTogglePicture}>
                Visualizar imagem
              </Button>
            </ContainerFlex>
          </ContainerFlex>
        </St.BoxCard>
      </Container>
    </>
  );
};

export default BankNowBiometricsDetail;
